import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Test Bench review findings with a "What to do" block per finding.
 *
 * The server attaches to every finding a `fix`: who can act (the brain owner, or iMaven), which
 * dashboard tool to use, a breadcrumb to the exact place, and the steps. Owner-side fixes get an
 * "Open in Synthora" button that lands on the right module; platform-side ones say so and show
 * that iMaven was notified. Synthora always edits the signed-in expert's own brain (a marketplace
 * storefront reads its owner's brain, so fixing the owner's brain fixes the storefront).
 */
const SEV = { high: '#ef4444', medium: '#f59e0b', low: '#64748b' };
const MODULE_FOR_TOOL = { 'opportunity-maps': 'opportunity-maps', 'widget-directive': 'agent-directives', 'training-mode': 'training-mode' };
const KIND_LABEL = { requirement: 'requirement', blocker: 'blocker', unlockFactor: 'unlock factor', universal: 'universal screening question' };

export default function ReviewFindings({ review, agentPagePath = '/agent' }) {
  const navigate = useNavigate();
  const findings = review?.findings || [];
  if (findings.length === 0) return null;

  const open = (fix) => {
    if (fix.tool === 'agent-persona') { navigate(agentPagePath); return; }
    const mod = MODULE_FOR_TOOL[fix.tool];
    if (!mod) return;
    navigate(`/Synthora?module=${mod}`);
  };

  return (
    <div style={{ marginTop: 10 }}>
      {findings.map((f, i) => {
        const fix = f.fix || {};
        const owner = fix.who !== 'imaven';
        const src = f.source;
        return (
          <div key={i} style={{ margin: '10px 0', padding: '10px 12px', background: 'rgba(0,0,0,0.2)', borderLeft: `3px solid ${SEV[f.severity] || SEV.low}`, borderRadius: 6, fontSize: 13, color: '#c9d1d9' }}>
            <div>
              <span style={{ fontSize: 10.5, textTransform: 'uppercase', color: '#94a3b8', marginRight: 6 }}>{f.severity}</span>
              <b>{f.title}</b>{f.turnIndex ? <span style={{ color: '#8b98a5' }}> (turn #{f.turnIndex})</span> : null}
            </div>
            {f.detail && <div style={{ marginTop: 4 }}>{f.detail}</div>}
            {src && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#8b98a5' }}>
                Comes from: {KIND_LABEL[src.kind] || src.kind}{src.itemName ? ` "${src.itemName}"` : ''}{src.pathwayName ? ` · pathway "${src.pathwayName}"` : ''} · map "{src.mapName}"{src.editable ? '' : ' · iMaven template (not editable by you)'}
              </div>
            )}
            <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 6, background: owner ? 'rgba(29,94,233,0.10)' : 'rgba(148,163,184,0.12)', border: `1px solid ${owner ? 'rgba(29,94,233,0.35)' : 'rgba(148,163,184,0.3)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <b style={{ fontSize: 12 }}>{owner ? 'What you can do' : "On iMaven's side"}</b>
                {owner && MODULE_FOR_TOOL[fix.tool] || fix.tool === 'agent-persona'
                  ? <button type="button" onClick={() => open(fix)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(29,94,233,0.5)', background: 'transparent', color: '#8ab4ff', cursor: 'pointer' }}>
                      {fix.tool === 'agent-persona' ? 'Open the agent' : 'Open in Synthora →'}
                    </button>
                  : null}
              </div>
              {fix.where && <div style={{ fontSize: 12, color: '#8b98a5', marginTop: 4 }}>{fix.where}</div>}
              {Array.isArray(fix.steps) && fix.steps.length > 0 && (
                <ol style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {fix.steps.map((s, j) => <li key={j} style={{ margin: '2px 0' }}>{s}</li>)}
                </ol>
              )}
              {!owner && (
                <div style={{ fontSize: 12, color: '#8b98a5', marginTop: 6 }}>
                  {review?.reportedToImavenAt ? `Reported to iMaven automatically on ${new Date(review.reportedToImavenAt).toLocaleString()}.` : 'Reported to iMaven automatically.'}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div style={{ fontSize: 12, color: '#8b98a5', marginTop: 8 }}>After a change, run the test again; earlier runs stay in the list for comparison.</div>
    </div>
  );
}
